/**
 * SOFSEK — Mitigation Control Templates (§20, within-tenant only — cross-tenant
 * sharing struck per amendment). Bundles a default reason code + a set of
 * mitigating controls so a risk director can one-click populate the
 * disposition wizard instead of re-typing the same recipe every time.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { buildTranslationsGroup } from '../../components/common/translations_group';
import { useActiveLocale } from '../../lib/use_active_locale';
import { TranslationsEditor } from '../../components/common/TranslationsEditor';
import type { TranslationRow } from '../../components/common/TranslationsEditor';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.MITIGATION_TEMPLATE.LIST';
const ACTIVE_LOCALES = ['EN_US', 'ES_MX', 'EN_GB'];
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

interface CodeOpt { code: string; name: string; }
interface TemplateRow {
  template_id: number;
  internal_code: string;
  default_reason_code: string | null;
  usage_count: number;
  is_active: 'Y' | 'N';
  tl: { locale_code: string; display_text: string }[];
}
interface TemplateControlRow { template_control_id: number; template_id: number; mitigating_action_code: string; }

function tlName(list: { locale_code: string; display_text: string }[], loc: string): string {
  return list.find(x => x.locale_code === loc)?.display_text ?? list.find(x => x.locale_code === 'EN_US')?.display_text ?? '';
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'display_text', header: 'Template', defaultWidth: 220 },
  { key: 'internal_code', header: 'Code', defaultWidth: 160 },
  { key: 'default_reason', header: 'Default Reason', defaultWidth: 200 },
  { key: 'usage_count', header: 'Uses', defaultWidth: 90 },
  { key: 'is_active', header: 'Active', defaultWidth: 90 },
];

function TemplateInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  // §17.B14 — names each language in the reader's own language.
  const locale = useActiveLocale();
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [templateControls, setTemplateControls] = useState<TemplateControlRow[]>([]);
  const [reasons, setReasons] = useState<CodeOpt[]>([]);
  const [controls, setControls] = useState<CodeOpt[]>([]);
  const [filter, setFilter] = useState('');
  const [activeFacet, setActiveFacet] = useState<string[]>(['Y']);
  const [editing, setEditing] = useState<TemplateRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const detail = useExpandedRows<number>();
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [tmplRes, tcRes, reasonRes, controlRes] = await Promise.all([
        apiDb.from('sfk_mitigation_template').select('template_id, internal_code, reason_code, usage_count, is_active, sfk_mitigation_template_tl ( locale_code, display_text )').order('internal_code'),
        apiDb.from('sfk_mitigation_template_control').select('template_control_id, template_id, mitigating_action_code'),
        apiDb.from('sfk_reason').select('reason_code, sfk_reason_tl ( locale_code, display_text )'),
        apiDb.from('sfk_mitigating_action').select('mitigating_action_code, sfk_mitigating_action_tl ( locale_code, display_text )'),
      ]);
      if (tmplRes.error) throw tmplRes.error;
      if (tcRes.error) throw tcRes.error;
      if (reasonRes.error) throw reasonRes.error;
      if (controlRes.error) throw controlRes.error;
      setTemplates(((tmplRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        template_id: r.template_id as number,
        internal_code: r.internal_code as string,
        default_reason_code: r.reason_code as string | null,
        usage_count: r.usage_count as number,
        is_active: r.is_active as 'Y' | 'N',
        tl: (r.sfk_mitigation_template_tl ?? []) as TemplateRow['tl'],
      })));
      setTemplateControls((tcRes.data ?? []) as unknown as TemplateControlRow[]);
      setReasons(((reasonRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.reason_code as string, name: tlName((r.sfk_reason_tl ?? []) as TemplateRow['tl'], 'EN_US') || (r.reason_code as string) })));
      setControls(((controlRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.mitigating_action_code as string, name: tlName((r.sfk_mitigating_action_tl ?? []) as TemplateRow['tl'], 'EN_US') || (r.mitigating_action_code as string) })));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

   
  useEffect(() => { void reload(); }, []);

  const resolveNameForDefaultReason = (code: string | null) => (code ? reasons.find(r => r.code === code)?.name ?? code : '—');
  const resolveNameForControl = (code: string) => controls.find(c => c.code === code)?.name ?? code;
  const controlsOf = (templateId: number) => templateControls.filter(tc => tc.template_id === templateId).map(tc => tc.mitigating_action_code);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return templates.filter(tp =>
      (!q || tp.internal_code.toLowerCase().includes(q) || tlName(tp.tl, 'EN_US').toLowerCase().includes(q)) &&
      (activeFacet.length === 0 || activeFacet.includes(tp.is_active)));
  }, [templates, filter, activeFacet]);

  const hasRelated = (id: number) => controlsOf(id).length > 0
    || (templates.find(r => r.template_id === id)?.tl.length ?? 0) > 0;
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => [
    {
      key: 'controls',
      title: t('sfk.mitigation_template.related.controls', 'Mitigating Controls'),
      items: controlsOf(id).map(code => ({ id: code, label: resolveNameForControl(code), title: code })),
    },

    buildTranslationsGroup(templates.find(r => r.template_id === id)?.tl, t, locale),
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.mitigation_template.title', 'Mitigation Control Templates')}
          description={t('sfk.mitigation_template.description', 'Reusable recipes of a default reason plus mitigating controls for the disposition wizard.')}
          actions={<>
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
            {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> {t('common.new', 'New')}</button>)}
          </>}
        />
        <StatStrip stats={[
          { value: templates.length, label: t('sfk.mitigation_template.stat.total', 'Templates') },
          { value: templates.filter(tp => tp.is_active === 'Y').length, label: t('common.boolean.active', 'Active') },
        ]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('common.boolean.active', 'Active')} options={[{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }]} selected={activeFacet} onChange={setActiveFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || activeFacet.length > 0} onClear={() => { setFilter(''); setActiveFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('sfk.mitigation_template.related.header', 'Mitigating controls')}></th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(tp => (
                  <Fragment key={tp.template_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={(e) => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(tp.template_id)}
                        hasChildren={hasRelated(tp.template_id)}
                        onToggle={() => detail.toggle(tp.template_id)}
                        expandLabel={t('sfk.mitigation_template.related.expand_aria', 'Show mitigating controls')}
                        collapseLabel={t('sfk.mitigation_template.related.collapse_aria', 'Hide controls')}
                        tooltip={t('sfk.mitigation_template.related.tooltip', 'Show mitigating controls')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'display_text' && tlName(tp.tl, 'EN_US')}
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{tp.internal_code}</span>} {/* fk-display-ok: template's own code */}
                        {col.key === 'default_reason' && resolveNameForDefaultReason(tp.default_reason_code)}
                        {col.key === 'usage_count' && tp.usage_count}
                        {col.key === 'is_active' && <YesNoBadge value={tp.is_active} />}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {canChange && (<button onClick={() => setEditing(tp)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.edit', 'Edit')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(tp.template_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(tp.template_id)} emptyLabel={t('sfk.mitigation_template.related.empty', 'No mitigating controls.')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <TemplateModal session={session} row={editing} reasons={reasons} controls={controls} existingControls={editing ? controlsOf(editing.template_id) : []}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}
    </div>
  );
}

function TemplateModal({ session, row, reasons, controls, existingControls, onClose, onSaved }: {
  session: AuthSession; row: TemplateRow | null; reasons: CodeOpt[]; controls: CodeOpt[]; existingControls: string[]; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const [code, setCode] = useState(row?.internal_code ?? '');
  const [defaultReason, setDefaultReason] = useState(row?.default_reason_code ?? '');
  const [isActive, setIsActive] = useState(!row || row.is_active === 'Y');
  const [selectedControls, setSelectedControls] = useState<string[]>(existingControls);
  const [translations, setTranslations] = useState<TranslationRow[]>(row?.tl ?? ACTIVE_LOCALES.map(loc => ({ locale_code: loc, display_text: '' })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleControl = (c: string) => setSelectedControls(cur => cur.includes(c) ? cur.filter(x => x !== c) : [...cur, c]);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!code.trim()) throw new Error(t('common.err.code_required', 'Code is required.'));
      if (!translations.some(tr => tr.display_text?.trim())) throw new Error(t('common.err.translation_required', 'At least one translation is required.'));

      const payload = { reason_code: defaultReason || null, is_active: isActive ? 'Y' : 'N' };
      let templateId = row?.template_id;
      if (row === null) {
        const { row_id, error: e } = await routedInsert(session, 'sfk_mitigation_template', { internal_code: code.trim().toUpperCase(), ...payload });
        if (e) throw new Error(e.message);
        templateId = row_id ?? undefined;
      } else {
        const { error: e } = await routedUpdate(session, 'sfk_mitigation_template', 'template_id', row.template_id, payload);
        if (e) throw new Error(e.message);
      }
      if (templateId != null) {
        await Promise.all(
          translations.filter(tr => tr.display_text?.trim()).map(tr =>
            routedInsert(session, 'sfk_mitigation_template_tl', { template_id: templateId, locale_code: tr.locale_code, display_text: tr.display_text }))
        );
        const toAdd = selectedControls.filter(c => !existingControls.includes(c));
        await Promise.all(toAdd.map(c => routedInsert(session, 'sfk_mitigation_template_control', { template_id: templateId, mitigating_action_code: c })));
      }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={isCreate ? t('sfk.mitigation_template.new', 'New Template') : `${t('common.edit', 'Edit')} — ${row?.internal_code ?? ''}`}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="internal_code" label={t('common.code', 'Code')}>
        <label className={labelCls}>{t('common.code', 'Code')}</label>
        <input value={code} disabled={!isCreate} onChange={e => setCode(e.target.value.toUpperCase())} className={`${inputCls} font-mono`} />
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="default_reason_code" label={t('sfk.mitigation_template.default_reason', 'Default Reason')}>
        <label className={labelCls}>{t('sfk.mitigation_template.default_reason', 'Default Reason')}</label>
        <select value={defaultReason} onChange={e => setDefaultReason(e.target.value)} className={`${inputCls} bg-white`}>
          <option value="">{t('common.none', '(none)')}</option>
          {reasons.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
        </select>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="is_active" label={t('common.boolean.active', 'Active')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 accent-gray-700" />
          {t('common.boolean.active', 'Active')}
        </label>
      </PersonalizedField>

      <TranslationsEditor availableLocales={ACTIVE_LOCALES} value={translations} onChange={setTranslations} label={t('common.translations', 'Translations')} />

      <div className="mb-3">
        <span className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('sfk.violation.controls', 'Mitigating Controls')}</span>
        <div className="max-h-40 overflow-auto rounded border border-csm-border">
          {controls.map(c => (
            <label key={c.code} className="flex items-center gap-2 border-b border-csm-border-soft px-2 py-1 text-csm-sm last:border-b-0">
              <input type="checkbox" checked={selectedControls.includes(c.code)} onChange={() => toggleControl(c.code)} className="h-4 w-4 accent-gray-700" />
              {c.name}
            </label>
          ))}
        </div>
      </div>

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function SfkMitigationTemplatePage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <TemplateInner session={session} />
    </PersonalizationProvider>
  );
}
